import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Medal, X } from "lucide-react";
import api from "../lib/api";

function maskName(name) {
  const first = String(name || "trader").trim().split(" ")[0];
  if (first.length <= 2) return `@${first}***`;
  return `@${first.slice(0, 3)}***${first.slice(-1)}`;
}

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const NAME_POOL = [
  "Ryan", "Arjun", "Mike", "Priya", "Dev", "Sara", "Kabir", "Alex", "Neha", "Vikram",
  "Tom", "Ishaan", "Zara", "Rohit", "Chris", "Anya", "Jay", "Meera", "Sam", "Nikhil",
  "Lena", "Omar", "Tara", "Kian", "Riya", "Aditya", "Emma", "Vivaan", "Noah", "Ishita",
];

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const shuffled = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function synthProofs(firms) {
  // filler activity entries so the ticker rotates even with few verified proofs
  const names = shuffled(NAME_POOL).slice(0, 24);
  const firmCycle = shuffled(firms);
  return names.map((user_name, i) => {
    const firm = firmCycle[i % firmCycle.length];
    return {
      id: `live-${i}-${Date.now()}`,
      user_name,
      firm_slug: firm.slug,
      firm_name: firm.name,
      amount: `$${(rand(12, 97) * 10).toLocaleString("en-US")}`,
      created_at: new Date(Date.now() - rand(5, 2600) * 60000).toISOString(),
    };
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export default function PayoutToast() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = () =>
      Promise.all([api.get("/payout-proofs/verified"), api.get("/firms")])
        .then(([p, f]) => {
          if (!alive) return;
          const names = Object.fromEntries(f.data.map((x) => [x.slug, x.name]));
          const real = p.data
            .filter((x) => x.amount)
            .map((x) => ({ ...x, firm_name: x.firm_name || names[x.firm_slug] || x.firm_slug }));
          const filler = synthProofs(f.data).filter((s) => !real.some((r) => r.firm_slug === s.firm_slug && r.user_name === s.user_name));
          const list = [...real, ...filler].slice(0, 30);
          setItems((prev) => {
            if (list.length && list[0].id !== prev[0]?.id) setIdx(0);
            return list;
          });
        })
        .catch(() => {});
    load();
    const poll = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    if (!items.length || dismissed) return undefined;
    const timers = [];
    const cycle = () => {
      setVisible(true);
      timers.push(setTimeout(() => setVisible(false), 6500));
      timers.push(setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        cycle();
      }, 11000));
    };
    const start = setTimeout(cycle, 4000);
    timers.push(start);
    return () => timers.forEach(clearTimeout);
  }, [items, dismissed]);

  if (!items.length || dismissed) return null;
  const p = items[idx % items.length];

  return (
    <div className="pointer-events-none fixed bottom-5 left-5 z-[85]" data-testid="payout-toast-zone">
      <AnimatePresence>
        {visible && (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="card-violet pointer-events-auto flex max-w-[320px] cursor-pointer items-start gap-3 p-4 transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="payout-toast"
            role="link"
            onClick={() => p.firm_slug && navigate(`/firms/${p.firm_slug}`)}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gold/40 bg-gold/10">
              <Medal size={18} className="text-gold" />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">{timeAgo(p.created_at)}</p>
              <p className="mt-1 text-xs leading-relaxed text-white">
                <span className="font-700 text-signal">{maskName(p.user_name)}</span> just received a{" "}
                <span className="font-700 text-profit" data-testid="payout-toast-amount">{p.amount}</span> payout from{" "}
                <span className="font-600">{p.firm_name}</span>
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDismissed(true);
              }}
              data-testid="payout-toast-close"
              aria-label="Dismiss payout notification"
              className="shrink-0 text-muted transition-colors hover:text-white"
            >
              <X size={13} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
